import { createContext, useContext } from 'react';

/** What `KioskScreen` hands the card inside it for a dead end's own help button. */
export interface DeadEndHelp {
  /** One tap: raise the entry with the screen's *Need help?* control, then show the overlay's queue state. */
  readonly askForHelp: () => void;
}

/**
 * ⭐ **Provided by `KioskScreen` to exactly the card it wraps** [03-us-kid-7 first scenario; 0230].
 *
 * A context rather than a store field, because the overlay belongs to ONE `KioskScreen` instance: a flag keyed
 * by screen id reopened it on the next component rendered under the same id (`CheckingScreen` →
 * `CheckInScreen`, both `checkIn`) [KID-7 review round 1]. The button sits BELOW `KioskScreen`, so the context
 * reaches it — unlike Exit, whose Back lives in the screen's own hooks above it (`exitAskedOn`).
 */
export const DeadEndHelpContext = createContext<DeadEndHelp | undefined>(undefined);

/**
 * The enclosing screen's help. ⛔ **Throws outside a `KioskScreen`**: a help button with nothing behind it is the
 * dead control KID-7 exists to remove, so a misplaced one fails loudly in a test rather than silently at a kiosk.
 */
export function useDeadEndHelp(): DeadEndHelp {
  const help = useContext(DeadEndHelpContext);
  if (help === undefined)
    throw new Error('DeadEndHelpButton must be rendered inside a KioskScreen');
  return help;
}
